/**
 * /publishers/caps — per-affiliate conversion / payout caps.
 *
 * Mirrors the contract declared by the API feature #135 (OPUS-135):
 *   - POST   /api/v1/publisher-caps
 *   - GET    /api/v1/publisher-caps/:affiliate_id
 *   - POST   /api/v1/publisher-caps/increment
 *   - POST   /api/v1/publisher-caps/check
 *
 * Declares a TanStack Router route and renders the headline + the
 * `PublisherCapsPage` shell. The route auto-registers against the
 * scaffolded `rootRoute` via a module augmentation in the scaffold's
 * `router.tsx` — any future agent / the root wire-up cycle picks it up
 * by importing from this module.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { PublisherCapsPage } from "@/features/publisher-caps/PublisherCapsPage";

// ---------------------------------------------------------------------------
// Route — mounts at `/publishers/caps`.
// `validateSearch` reserves room for an `?affiliateId=` deep-link so
// operators can open the page from a publisher detail view with the ID
// prefilled.
// ---------------------------------------------------------------------------

interface PublisherCapsSearch {
	affiliateId?: string;
}

export const publishersCapsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/publishers/caps",
	validateSearch: (raw: Record<string, unknown>): PublisherCapsSearch => {
		const affiliateId = raw.affiliateId;
		if (typeof affiliateId === "string" && affiliateId.trim().length > 0) {
			return { affiliateId: affiliateId.trim() };
		}
		return {};
	},
	component: PublishersCapsRouteComponent,
});

export default publishersCapsRoute;

// ---------------------------------------------------------------------------
// Page — static shell + the publisher-caps operator surface.
// ---------------------------------------------------------------------------

export function PublishersCapsRouteComponent() {
	const { affiliateId } = publishersCapsRoute.useSearch();

	return (
		<main className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-10 sm:py-16">
			<Breadcrumb />
			<Heading />
			<PublisherCapsPage initialAffiliateId={affiliateId} />
			<DocHint />
		</main>
	);
}

// ---------------------------------------------------------------------------
// Header fragments — purely presentational. No emojis, no competitor names.
// ---------------------------------------------------------------------------

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Publishers</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Caps</li>
			</ol>
		</nav>
	);
}

function Heading() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Publishers
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				Conversion & payout caps
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Throttle how much any single publisher can deliver — in raw
				conversions or in payout — across daily, monthly, or lifetime
				windows. The tracker checks these caps before attribution so
				spend never overshoots.
			</p>
		</header>
	);
}

function DocHint() {
	return (
		<aside className="rounded-[8px] border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
			<p className="font-medium text-zinc-800">How it works</p>
			<ul className="mt-2 flex list-inside list-disc flex-col gap-1">
				<li>
					One row per (publisher, offer, cap type, window). Leave the offer
					blank to cap the publisher across every offer at once.
				</li>
				<li>
					/check returns allowed=false whenever any governing cap has
					consumed ≥ limit — paused caps never block.
				</li>
				<li>
					Conversions POST /increment on commit — the response signals
					over-cap so downstream analytics stay consistent.
				</li>
			</ul>
		</aside>
	);
}
